import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Formik, Form, Field } from 'formik';
import { useListProductsQuery } from 'renderer/features/products-page/services/products.endpoints';
import { formatQueryError } from 'renderer/shared/formatters/formatters';
import OrderSummary from 'renderer/features/purchase-page/components/OrderSummary';
import HostAppsDropdown from 'renderer/features/purchase-page/components/HostAppsDropdown';
import { useCreateResellerCodeMutation } from '../services/resellerCodes.endpoints';

interface ResellerCodeFormProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const ResellerCodeForm = ({ open, setOpen }: ResellerCodeFormProps) => {
  const {
    data: products,
    isLoading: productsLoading,
    error: productsError,
  } = useListProductsQuery({ includeHidden: true });

  const [
    createResellerCodes,
    { isLoading: createLoading, error: createError, isSuccess: createSuccess },
  ] = useCreateResellerCodeMutation();

  const isLoading = productsLoading || createLoading;
  const error = createError || productsError;
  console.log({ createError, productsError });

  const [localSuccess, setLocalSuccess] = useState(false);

  const handleDownload = (
    codes: string[],
    resellerName: string,
    productName: string,
  ) => {
    // Get the local date in YYYY-MM-DD format
    const now = new Date();
    const dateCreated = `${now.getFullYear()}-${String(
      now.getMonth() + 1,
    ).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    const fileName = `${resellerName}_${productName}_${dateCreated}.txt`
      .replace(/\s+/g, '_')
      .replace(/[^a-zA-Z0-9_\-.]/g, '');
    const fileContent = codes.join('\n');
    const blob = new Blob([fileContent], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
  };

  const handleSubmit = async (values: any) => {
    try {
      const response = await createResellerCodes(values).unwrap();
      if (response.codes) {
        const resellerName = values.resellerName || 'Reseller';
        const productName =
          products?.find((product) => product._id === values.productId)?.name ||
          'Product';
        handleDownload(response.codes, resellerName, productName);
        setLocalSuccess(true);
      }
    } catch (err) {
      console.error('Error generating reseller codes:', err);
    }
  };

  useEffect(() => {
    if (!open) {
      setLocalSuccess(false); // Clear local success message when dialog closes
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
      <DialogTitle>Create Reseller Codes</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={() => setOpen(false)}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent>
        <DialogContentText>
          Select a product and the number of reseller codes to generate. You can
          also specify platform and host application.
        </DialogContentText>
        <Formik
          initialValues={{
            productId: '',
            copies: 1,
            platform: '',
            resellerName: '',
            hostApplication: { id: '', version: '', displayName: '' }, // Initialize hostApplication properly
          }}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="resellerName"
                    label="Reseller Name"
                    fullWidth
                    required
                  />
                </Grid>
                {products && (
                  <Grid item xs={12}>
                    <Field
                      as={TextField}
                      select
                      name="productId"
                      label="Product"
                      fullWidth
                      required
                      disabled={isLoading}
                      value={values.productId}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        const newProductId = event.target.value;
                        setFieldValue('productId', newProductId);

                        // reset hostApp when product changes
                        setFieldValue('hostApplication', {
                          id: '',
                          version: '',
                          displayName: '',
                        });
                      }}
                    >
                      {products
                        .filter(({ category }) => category === 'Subscription')
                        .map(({ _id, name }) => (
                          <MenuItem key={_id} value={_id}>
                            {name}
                          </MenuItem>
                        ))}
                    </Field>
                  </Grid>
                )}

                {/* HostAppsDropdown appears once a product is selected */}
                {values.productId &&
                  (() => {
                    const selectedProduct = products?.find(
                      (p) => p._id === values.productId,
                    );

                    const supportedHostApps =
                      selectedProduct?.supportedHostApplications || [];

                    return (
                      <Grid item xs={12}>
                        <HostAppsDropdown
                          supportedHostApplications={supportedHostApps}
                          value={values.hostApplication}
                          onChange={(hostApp) =>
                            setFieldValue('hostApplication', hostApp)
                          }
                        />
                      </Grid>
                    );
                  })()}

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    select
                    name="platform"
                    label="Platform"
                    fullWidth
                    required
                    value={values.platform}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setFieldValue('platform', event.target.value);
                    }}
                  >
                    <MenuItem value="Macintosh">Macintosh</MenuItem>
                    <MenuItem value="Windows">Windows</MenuItem>
                  </Field>
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    type="number"
                    name="numberOfCopies"
                    label="Number of Copies"
                    fullWidth
                    required
                    disabled={isLoading}
                    inputProps={{ min: 1 }}
                  />
                </Grid>

                {/* {values.productId && (
                  <Grid item xs={12}>
                    <OrderSummary
                      product={products?.find(
                        (product) => product._id === values.productId,
                      )}
                      hidePricing
                      hidePlatform
                    />
                  </Grid>
                )} */}
                {error && (
                  <Grid item xs={12}>
                    <Alert severity="error">
                      Error creating reseller codes: {formatQueryError(error)}
                    </Alert>
                  </Grid>
                )}
                {localSuccess && (
                  <Grid item xs={12}>
                    <Alert severity="success">
                      Reseller codes created successfully!
                    </Alert>
                  </Grid>
                )}
              </Grid>
              <DialogActions>
                <Button onClick={() => setOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? <CircularProgress size={24} /> : 'Create'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default ResellerCodeForm;
